// THE FREE CHECK ON THE PURCHASE PAGE: THE FIRST-LOOK BAND AND THE WHOLE CHECK IN A POP-UP (R-pending-web-free-first;
// Tyler, 2026-10-14: "A" — app.proppeal.com shows the free check's details before purchase — and of the layout, "They put
// their address in and when it takes them to the purchase or use credit page, it gracefully displays the free stuff", then
// "Yes on layout").
//
// A RENDERER OF THE SERVER'S FREE CHECK, AND NOTHING ELSE (the thin-client rule). v2ResolveProperty op `freeCheck` runs the
// one free check — the iPhone app's and proppeal.com's — for the appeal the page holds, and answers its screen, its extras
// and its first look. Every sentence drawn here is one of those, as sent; the page writes no free-check sentence of its own
// (`test/entry.test.tsx` searches this package for every one in the server's table). The words that are the page's are the
// app's checker's own (`prePayCopy.ts`: the title, "Running your free check…", "Close") and the link's
// (approved by Tyler 2026-10-15, `heldCopy.ts` `FREE_FIRST_LINK_COPY`).
//
// THE BAND sits in the "Before you pay" panel under its top row, in both of the panel's layouts: the title, the server's
// caption, up to four rows with small symbols, and the link. While the server is asked, it holds its place with the app's
// "Running your free check…" and two grey rows, so the Buy button never jumps; on a failure, a refusal or no answer
// within 45 seconds it is gone, silently (`Checkout.tsx`), and the purchase is exactly as it was.
//
// THE POP-UP is the whole free check in the app's order (`freeFirstLayout.ts`): the deadline card with "Add to calendar"
// (an .ics made on the device) and the amber button; the screen's lines, the guide line a link to proppeal.com in a new
// tab; the extras' cards; the estimator, computed on the device from what the owner types and sent nowhere; "Full case
// analysis: not completed…"; and the close. EVERY BUTTON INTO THE PAID FLOW closes the pop-up and does what this page does
// next — nothing goes to Stripe from here (`onProceed`). No satellite view and no street photo: both are held.

import { useId, useRef, useState, type ReactNode, type RefObject } from "react";
import type { FreeCheckExtras, FreeCheckFirstLook, FreeCheckLine, FreeCheckScreen } from "@contract";
import { estimatorLines, estimatorOutcome, parseOwnerDollars } from "@v2-freecheck-estimator";
import { freeFirstLinkWords } from "../app/freeFirst";
import { prePayText } from "../components/prePayCopy";
import {
  MONEY_RULES, PER_10K_RULES, PROCEED_LINE_ID, SHARE_RULES, URGENT_DAYS, closeParts, groupRuns, icsFile, lineGroup, linkUrl, panelSections,
  runs, sourcedGroups, sparkPoints, type Section,
} from "./freeFirstLayout";
import { PopUp } from "./popUp";
import { CalendarIcon, UptrendIcon } from "./prePayPanel";
import { Icon } from "./steps/parity4Parts";

// ── The band ─────────────────────────────────────────────────────────────────────────────────────────────────────────

function RowIcon({ kind }: { kind: string }) {
  if (kind === "deadline") return <CalendarIcon size={16} />;
  if (kind === "history") return <UptrendIcon size={16} />;
  if (kind === "lastSale") return <Icon name="doc" size={16} />;
  return <Icon name="house" size={16} />;
}

/** One server line, as text, carrying its id. */
function ServerText({ line, className }: { line: FreeCheckLine; className?: string }) {
  return <span className={className} data-free-check-line={line.id}>{line.text}</span>;
}

/**
 * THE BAND. `look` null: still asking — the placeholder, holding the band's height. Otherwise the server's first look.
 * The band is never drawn for a failure or a refusal: the page draws nothing at all then.
 */
export function FreeFirstBand({ look, onOpen, linkRef }: {
  look: FreeCheckFirstLook | null;
  onOpen: () => void;
  linkRef: RefObject<HTMLButtonElement | null>;
}) {
  const headingId = useId();
  const loading = look === null;
  const link = freeFirstLinkWords();
  return (
    <section
      className="free-first"
      aria-labelledby={headingId}
      aria-busy={loading}
      data-free-first={loading ? "loading" : "ready"}
    >
      <h3 id={headingId} className="free-first-title" data-app-copy="freeFirst.title">{prePayText("freeFirst.title")}</h3>
      {loading ? (
        <>
          <p className="free-first-caption" role="status" data-app-copy="freeFirst.running">{prePayText("freeFirst.running")}</p>
          <ul className="free-first-rows" aria-hidden="true" data-free-first-skeleton>
            <li className="free-first-row skeleton"><span className="free-first-icon" /><span className="skeleton-bar" /></li>
            <li className="free-first-row skeleton"><span className="free-first-icon" /><span className="skeleton-bar short" /></li>
          </ul>
        </>
      ) : (
        <>
          {look.caption !== null && <p className="free-first-caption"><ServerText line={look.caption} /></p>}
          <ul className="free-first-rows">
            {look.rows.map((row, i) => (
              <li key={`${row.kind}-${i}`} className="free-first-row" data-first-look-row={row.kind}>
                <span className="free-first-icon" aria-hidden="true"><RowIcon kind={row.kind} /></span>
                <span className="free-first-row-text">
                  {row.lines.map((line, j) => (
                    <ServerText key={line.id} line={line} className={j === 0 ? "free-first-fact" : "free-first-detail"} />
                  ))}
                </span>
              </li>
            ))}
          </ul>
          <button
            ref={linkRef}
            type="button"
            className="free-first-link"
            data-held-copy={link.id}
            aria-haspopup="dialog"
            onClick={onOpen}
          >
            {link.text}
          </button>
        </>
      )}
    </section>
  );
}

// ── The pop-up ───────────────────────────────────────────────────────────────────────────────────────────────────────

/** A server line on a card: a link when it carries an https href (opening proppeal.com in a new tab), else text, with the
 *  app's emphasis runs where `rules` name them. The text is never changed. */
function CardLine({ line, className, rules }: {
  line: FreeCheckLine;
  className?: string;
  rules?: readonly (readonly [RegExp, "strong" | "money"])[];
}) {
  const href = linkUrl(line.href);
  if (href !== null) {
    return (
      <p className={className === undefined ? "fc-line fc-link" : `fc-line fc-link ${className}`}>
        <a href={href} target="_blank" rel="noopener noreferrer">
          <span data-free-check-line={line.id}>{line.text}</span>
          <span className="fc-external" aria-hidden="true">↗</span>
        </a>
      </p>
    );
  }
  return (
    <p className={className === undefined ? "fc-line" : `fc-line ${className}`} data-free-check-line={line.id}>
      {rules === undefined
        ? line.text
        : runs(line.text, rules).map((run, i) =>
          run.emphasis === null ? <span key={i}>{run.text}</span> : <strong key={i} className={`fc-${run.emphasis}`}>{run.text}</strong>)}
    </p>
  );
}

function Eyebrow({ text }: { text: string }) {
  return <h4 className="fc-eyebrow">{text}</h4>;
}

function Card({ children, className, kind }: { children: ReactNode; className?: string; kind: string }) {
  return <div className={className === undefined ? "fc-card" : `fc-card ${className}`} data-fc-section={kind}>{children}</div>;
}

/** The amber button into the paid flow: the server's own words, and this page's next step (never Stripe). */
function ProceedButton({ line, onProceed, large = false }: { line: FreeCheckLine; onProceed: () => void; large?: boolean }) {
  return (
    <button
      type="button"
      className={large ? "btn primary block fc-proceed large" : "btn primary block fc-proceed"}
      data-free-check-line={line.id}
      data-fc-proceed
      onClick={onProceed}
    >
      {large && <Icon name="shield" size={18} />}
      {line.text}
    </button>
  );
}

/** Saves the deadline's .ics, made here from the server's title and date. False where the browser cannot (no object URL). */
function saveIcs(calendar: { title: string; date: string }, now: Date, doc: Document = document): boolean {
  const file = icsFile(calendar, now);
  if (file === null || typeof URL.createObjectURL !== "function") return false;
  const url = URL.createObjectURL(new Blob([file.text], { type: "text/calendar;charset=utf-8" }));
  const a = doc.createElement("a");
  a.href = url;
  a.download = file.name;
  a.rel = "noopener";
  doc.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
}

function DeadlineCard({ deadline, labels, proceed, onProceed }: {
  deadline: NonNullable<FreeCheckExtras["deadline"]>;
  labels: FreeCheckExtras["labels"];
  proceed: FreeCheckLine | null;
  onProceed: () => void;
}) {
  const urgent = deadline.daysLeft <= URGENT_DAYS;
  return (
    <Card kind="deadline" className={urgent ? "fc-deadline urgent" : "fc-deadline"}>
      <Eyebrow text={labels.deadline} />
      {deadline.lines.map((line, i) => (
        <CardLine key={line.id} line={line} className={i === 0 ? "fc-big" : "fc-small"} />
      ))}
      <button type="button" className="btn ghost fc-calendar" onClick={() => saveIcs(deadline.calendar, new Date())}>
        <CalendarIcon size={16} />
        <span>{labels.addToCalendar}</span>
      </button>
      {proceed !== null && <ProceedButton line={proceed} onProceed={onProceed} />}
    </Card>
  );
}

function LinesCard({ lines, onProceed }: { lines: readonly FreeCheckLine[]; onProceed: () => void }) {
  return (
    <Card kind="lines">
      {groupRuns(lines).map((group, g) => {
        const kind = lineGroup(group[0].id);
        if (kind === "record") {
          return (
            <div key={g} className="fc-record">
              {group.map((line) => <CardLine key={line.id} line={line} />)}
            </div>
          );
        }
        if (kind === "notice") {
          return (
            <div key={g} className="fc-notice">
              <Icon name="info" size={16} />
              <div>{group.map((line) => <CardLine key={line.id} line={line} />)}</div>
            </div>
          );
        }
        return group.map((line) =>
          line.id === PROCEED_LINE_ID
            ? <ProceedButton key={line.id} line={line} onProceed={onProceed} />
            : <CardLine key={line.id} line={line} className={kind === "lead" ? "fc-lead" : undefined} />);
      })}
    </Card>
  );
}

function SourcedCard({ heading, lines, sourceLine, sourceUrl, headline, kind }: {
  heading: string | undefined;
  lines: readonly FreeCheckLine[];
  sourceLine: FreeCheckLine;
  sourceUrl: string;
  headline: boolean;
  kind: string;
}) {
  const href = linkUrl(sourceUrl);
  return (
    <Card kind={kind}>
      {heading !== undefined && <Eyebrow text={heading} />}
      {sourcedGroups(lines).map((group, g) =>
        group.length > 1 ? (
          <div key={g} className="fc-together">{group.map((line) => <CardLine key={line.id} line={line} />)}</div>
        ) : (
          <CardLine key={g} line={group[0]} className={headline && g === 0 ? "fc-big" : undefined} />
        ))}
      {href !== null ? (
        <p className="fc-line fc-small fc-source">
          <a href={href} target="_blank" rel="noopener noreferrer" data-free-check-line={sourceLine.id}>{sourceLine.text}</a>
        </p>
      ) : (
        <CardLine line={sourceLine} className="fc-small" />
      )}
    </Card>
  );
}

function TaxCostCard({ card, heading }: { card: NonNullable<FreeCheckExtras["taxCost"]>; heading: string }) {
  const big = card.annualTax !== null && card.lines.length >= 2;
  return (
    <Card kind="taxCost">
      <Eyebrow text={heading} />
      {card.lines.map((line, i) =>
        big && i === 0 ? <CardLine key={line.id} line={line} className="fc-big" />
          : i === card.lines.length - 1 ? <CardLine key={line.id} line={line} rules={PER_10K_RULES} />
            : <CardLine key={line.id} line={line} className="fc-dim" />)}
    </Card>
  );
}

function ExemptionsCard({ items, labels }: { items: NonNullable<FreeCheckExtras["exemptions"]>; labels: FreeCheckExtras["labels"] }) {
  return (
    <Card kind="exemptions">
      <Eyebrow text={labels.exemptions} />
      {items.map((item) => {
        const lines = item.lines.filter((l) => l.text !== item.name);
        const linked = lines.some((l) => linkUrl(l.href) !== null);
        const apply = linkUrl(item.applyUrl);
        return (
          <div key={item.key} className="fc-exemption">
            <p className="fc-exemption-head">
              <strong>{item.name}</strong>
              {item.status === "notOnRecord" && <span className="fc-tag">{labels.notOnRecord}</span>}
            </p>
            {lines.map((line) => <CardLine key={line.id} line={line} className="fc-dim" rules={MONEY_RULES} />)}
            {!linked && apply !== null && (
              <p className="fc-line"><a href={apply} target="_blank" rel="noopener noreferrer">{labels.howToApply}</a></p>
            )}
          </div>
        );
      })}
    </Card>
  );
}

function PriceTrendCard({ card, heading }: { card: NonNullable<FreeCheckExtras["priceTrend"]>; heading: string }) {
  const [first, ...rest] = card.lines;
  const points = sparkPoints(card.series, 120, 44);
  return (
    <Card kind="priceTrend">
      <Eyebrow text={heading} />
      <div className="fc-trend">
        {first !== undefined && <CardLine line={first} className={card.changePct > 0 ? "fc-big fc-up" : "fc-big"} />}
        {points !== "" && (
          <svg className="fc-spark" width={120} height={44} viewBox="0 0 120 44" aria-hidden="true" focusable="false">
            <polyline points={points} fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      {rest.map((line) => <CardLine key={line.id} line={line} className="fc-dim" />)}
    </Card>
  );
}

/** "What could you save?": computed as the owner types, on this page only. The figures are this card's own state and go
 *  nowhere — no request, no storage, no log — and are gone when the pop-up closes. */
function EstimatorCard({ card, heading }: { card: FreeCheckExtras["estimator"]; heading: string }) {
  const t = card.templates;
  const [notice, setNotice] = useState("");
  const [worth, setWorth] = useState("");
  const [ticked, setTicked] = useState<ReadonlySet<number>>(new Set());
  const noticeId = useId();
  const worthId = useId();
  const assessed = card.assessedValue ?? parseOwnerDollars(notice);
  const worthValue = parseOwnerDollars(worth);
  const items = card.assessedValue !== null ? t.wrongItems : [];
  const outcome = estimatorOutcome({
    assessedValue: assessed, taxRate: card.taxRate ?? 0, ratio: card.ratio, worth: worthValue,
    exemptionAmount: card.exemptionAmount ?? null,
  });
  const lines = estimatorLines(outcome, worthValue, ticked.size, t);
  return (
    <Card kind="estimator" className="fc-estimator">
      <Eyebrow text={heading} />
      {card.assessedValue === null && (
        <div className="fc-field">
          <label htmlFor={noticeId}>{t.noticeLabel}</label>
          <input id={noticeId} inputMode="numeric" autoComplete="off" value={notice} onChange={(e) => setNotice(e.target.value)} />
          <p className="fc-line fc-small">{t.noticeHelp}</p>
        </div>
      )}
      <div className="fc-field">
        <label htmlFor={worthId}>{t.worthLabel}</label>
        <input id={worthId} inputMode="numeric" autoComplete="off" value={worth} onChange={(e) => setWorth(e.target.value)} />
      </div>
      {items.length > 0 && (
        <fieldset className="fc-wrong">
          <legend>{t.wrongLabel}</legend>
          {items.map((item, i) => (
            <label key={item} className="fc-check">
              <input
                type="checkbox"
                checked={ticked.has(i)}
                onChange={(e) => {
                  const next = new Set(ticked);
                  if (e.target.checked) next.add(i);
                  else next.delete(i);
                  setTicked(next);
                }}
              />
              <span>{item}</span>
            </label>
          ))}
        </fieldset>
      )}
      <div aria-live="polite" data-estimator-state={outcome.state}>
        {outcome.state === "saving" && (
          <div className="fc-saving">
            {lines.map((text, i) => <p key={i} className={i === 0 ? "fc-line fc-big fc-money" : i === 1 ? "fc-line" : "fc-line fc-small"}>{text}</p>)}
          </div>
        )}
        {outcome.state === "noSaving" && lines.map((text, i) => <p key={i} className="fc-line fc-dim">{text}</p>)}
      </div>
      <p className="fc-line fc-small">{t.disclaimer}</p>
    </Card>
  );
}

/** The close's picture of what unlocking gives: blurred bars and tiles, the same for every home, with a lock. NO WORDS and
 *  no figures — nothing a reader could take for the owner's data — and hidden from assistive technology. */
function LockedArt() {
  return (
    <svg className="fc-locked-art" viewBox="0 0 320 150" aria-hidden="true" focusable="false" data-locked-art>
      <rect x="0" y="0" width="320" height="108" rx="14" fill="#ffffff" />
      <g fill="#c3c8d8">
        <rect x="16" y="14" width="70" height="6" rx="3" /><rect x="150" y="14" width="40" height="6" rx="3" /><rect x="262" y="14" width="40" height="6" rx="3" />
      </g>
      <g fill="#dde0ea" style={{ filter: "blur(3px)" }}>
        {[34, 52, 70, 88].map((y) => (
          <g key={y}>
            <rect x="16" y={y} width="104" height="8" rx="4" /><rect x="142" y={y} width="58" height="8" rx="4" /><rect x="258" y={y} width="44" height="8" rx="4" />
          </g>
        ))}
      </g>
      <circle cx="160" cy="58" r="22" fill="#03093b" />
      <path d="M152 58v-6a8 8 0 0 1 16 0v6" fill="none" stroke="#fca311" strokeWidth="3" />
      <rect x="148" y="57" width="24" height="16" rx="3" fill="#fca311" />
      <rect x="0" y="118" width="154" height="32" rx="10" fill="rgba(2,6,21,0.5)" />
      <rect x="166" y="118" width="154" height="32" rx="10" fill="rgba(2,6,21,0.5)" />
      <g fill="#aeb7d6" style={{ filter: "blur(2px)" }}>
        <rect x="12" y="130" width="80" height="8" rx="4" /><rect x="178" y="130" width="80" height="8" rx="4" />
      </g>
    </svg>
  );
}

function CloseCard({ card, onProceed }: { card: FreeCheckExtras["close"]; onProceed: () => void }) {
  const parts = closeParts(card);
  if (parts === null) return null;
  return (
    <Card kind="close" className="fc-close">
      <h4 className="fc-close-title" data-free-check-line={parts.title.id}>{parts.title.text}</h4>
      {parts.body.map((line) => <CardLine key={line.id} line={line} className="fc-dim" />)}
      <LockedArt />
      {parts.unlock !== null && <ProceedButton line={parts.unlock} onProceed={onProceed} large />}
    </Card>
  );
}

function SectionView({ section, extras, onProceed }: { section: Section; extras: FreeCheckExtras | null; onProceed: () => void }) {
  const labels = extras?.labels;
  switch (section.kind) {
    case "deadline":
      return <DeadlineCard deadline={section.deadline} labels={labels!} proceed={section.proceed} onProceed={onProceed} />;
    case "lines":
      return <LinesCard lines={section.lines} onProceed={onProceed} />;
    case "rollChange":
      return <SourcedCard kind="rollChange" heading={labels?.rollChange} lines={section.card.lines} sourceLine={section.card.sourceLine} sourceUrl={section.card.source.url} headline={false} />;
    case "taxCost":
      return <TaxCostCard card={section.card} heading={labels!.taxCost} />;
    case "exemptions":
      return <ExemptionsCard items={section.items} labels={labels!} />;
    case "priceTrend":
      return <PriceTrendCard card={section.card} heading={labels!.priceTrend} />;
    case "homeValues":
      return <SourcedCard kind="homeValues" heading={labels?.homeValues} lines={section.card.lines} sourceLine={section.card.sourceLine} sourceUrl={section.card.source.url} headline />;
    case "appealRate":
      return (
        <Card kind="appealRate">
          <Eyebrow text={labels!.appealRate} />
          {section.card.lines.map((line) => <CardLine key={line.id} line={line} rules={SHARE_RULES} />)}
        </Card>
      );
    case "estimator":
      return section.card.taxRate === null ? null : <EstimatorCard card={section.card} heading={labels!.estimator} />;
    case "notDone":
      return <CardLine line={section.line} className="fc-small fc-not-done" />;
    case "close":
      return <CloseCard card={section.card} onProceed={onProceed} />;
  }
}

/**
 * THE WHOLE FREE CHECK, IN THE APP'S ORDER, AS A POP-UP: the site's pop-up (`PopUp`: Escape closes, the keyboard stays
 * inside), a full-height sheet on a phone. "Close" and Escape close it; every button into the paid flow closes it and runs
 * `onProceed`, the page's own next step.
 */
export function FreeCheckPanel({ screen, onClose, onProceed, returnTo }: {
  screen: FreeCheckScreen;
  onClose: () => void;
  onProceed: () => void;
  returnTo: RefObject<HTMLElement | null>;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const headingId = useId();
  return (
    <PopUp labelledBy={headingId} kind="freeCheck" heading={heading} onEscape={onClose} returnTo={returnTo} className="sheet-full fc-panel">
      <div className="fc-panel-head">
        <h2 id={headingId} ref={heading} tabIndex={-1} data-app-copy="freeFirst.title">{prePayText("freeFirst.title")}</h2>
        <button type="button" className="btn ghost fc-panel-close" data-app-copy="freeFirst.close" onClick={onClose}>
          {prePayText("freeFirst.close")}
        </button>
      </div>
      <div className="fc-sections">
        {panelSections(screen).map((section, i) => (
          <SectionView key={`${section.kind}-${i}`} section={section} extras={screen.extras} onProceed={onProceed} />
        ))}
      </div>
    </PopUp>
  );
}
